import { type ReactNode, useRef } from 'react';

import { useOutletContext, useRouteLoaderData } from '@remix-run/react';
import { useQueryClient } from '@tanstack/react-query';

import { type PostLike, postInsightsKeys } from '~/hooks/useLoaders/usePostInsights';
import usePostLikeState from '~/hooks/usePostLikeState';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { RootLoaderData } from '~/root';
import toast from '~/utils/toast';

import useGlobalModals from '../GlobalModals/hooks/useGlobalModals';

import ActionForm from '../ActionForm';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';

type RenderProps = {
  isLoading: boolean;
  isLiked: boolean;
  likesCount: number;
};

type Props = {
  children: (props: RenderProps) => ReactNode;
  postId: string;
  likes: PostLike[];
  areLikesFetched: boolean;
  onSuccess?: () => void;
};

const PostLikeAction = ({ children, likes, areLikesFetched, postId, onSuccess }: Props) => {
  const fetcher = useComponentFetcher();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);
  const { handleOpenModal } = useGlobalModals();

  const { admin } = useOutletContext<OutletContext>();
  const { subscription } = useRouteLoaderData('root') as RootLoaderData;
  const isAdminLoggedIn = Boolean(admin);
  const isNotLoggedIn = !isAdminLoggedIn && !subscription;

  const { isLiked, likesCount, toggle } = usePostLikeState({
    postId,
    likes,
    areLikesFetched,
    admin,
    subscription,
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isNotLoggedIn) {
      handleOpenModal('login');
      return;
    }

    toggle();

    const formData = new FormData(e.currentTarget);

    if (isAdminLoggedIn) {
      fetcher.submit(formData, { method: 'post', action: '/admins/post_like' });
    } else {
      fetcher.submit(formData, { method: 'post', action: '/post_like' });
    }
  };

  return (
    <ActionForm
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action={isAdminLoggedIn ? '/admins/post_like' : '/post_like'}
      onSubmit={handleSubmit}
      onSuccess={() => {
        // Refetch insights so the server like count reconciles with the optimistic
        // state (replaces the Remix fetcher's automatic post-mutation revalidation).
        queryClient.invalidateQueries({ queryKey: postInsightsKeys.byPost(postId) });
        onSuccess?.();
      }}
      onError={(data) => {
        toast.error(data?.toast?.message || 'Something went wrong, please try again');
      }}
    >
      <input type="hidden" name="post_id" value={postId} />
      <input type="hidden" name="subscription_id" value={subscription?.id} />
      <input type="hidden" name="user_id" value={admin?.id} />
      {children({ isLoading: fetcher.state === 'submitting', isLiked, likesCount })}
    </ActionForm>
  );
};

export default PostLikeAction;
